import { liftListItem, wrapInList } from 'prosemirror-schema-list';
import { closeHistory } from 'prosemirror-history';
import { Selection } from '@chatwoot/prosemirror-schema';

// A hard break is an inline node, so the standard list command wraps every
// line in its paragraph. Isolate the cursor's line before running that command.
export const wrapCurrentLineInList = (nodeType, attrs) => (state, dispatch) => {
  const { selection } = state;
  const { $from } = selection;
  const listRange = $from.blockRange(
    selection.$to,
    node =>
      node.childCount > 0 &&
      node.firstChild.type === state.schema.nodes.list_item
  );
  if (listRange) {
    let lifted;
    if (
      !liftListItem(state.schema.nodes.list_item)(state, tr => {
        lifted = closeHistory(tr);
      })
    )
      return false;

    if (listRange.parent.type === nodeType) {
      if (dispatch) dispatch(lifted.scrollIntoView());
      return true;
    }

    // Lift only the selected item(s), then wrap them in the other list type.
    // Keep both native operations in one transaction for a single undo step.
    return wrapInList(nodeType, attrs)(state.apply(lifted), listTr => {
      listTr.steps.forEach(step => lifted.step(step));
      lifted.setSelection(
        Selection.near(lifted.doc.resolve(listTr.selection.from))
      );
      lifted.setStoredMarks(state.storedMarks || $from.marks());
      if (dispatch) dispatch(lifted.scrollIntoView());
    });
  }
  if (
    !selection.empty ||
    $from.parent.type !== state.schema.nodes.paragraph ||
    Array.from({ length: $from.depth }, (_, depth) =>
      $from.node(depth + 1)
    ).some(node => node.type === state.schema.nodes.list_item)
  ) {
    return wrapInList(nodeType, attrs)(state, dispatch);
  }

  let start = 0;
  let end = $from.parent.content.size;
  $from.parent.forEach((node, offset) => {
    if (node.type !== state.schema.nodes.hard_break) return;
    if (offset < $from.parentOffset) start = offset + node.nodeSize;
    else if (end === $from.parent.content.size) end = offset;
  });
  if (!start && end === $from.parent.content.size) {
    return wrapInList(nodeType, attrs)(state, dispatch);
  }

  const tr = closeHistory(state.tr);
  // Split at the line boundaries, from right to left. Existing inline nodes
  // and their marks stay intact; only the boundary hard breaks become blocks.
  if (end < $from.parent.content.size) {
    const boundary = $from.start() + end;
    tr.delete(boundary, boundary + 1).split(boundary);
  }
  if (start) {
    const boundary = $from.start() + start - 1;
    tr.delete(boundary, boundary + 1).split(boundary);
  }
  const lineStart = tr.mapping.map($from.start() + start);
  const cursor = lineStart + $from.parentOffset - start;
  tr.setSelection(Selection.near(tr.doc.resolve(cursor)));
  const isolatedState = state.apply(tr);
  return wrapInList(nodeType, attrs)(
    isolatedState,
    dispatch &&
      (listTr => {
        listTr.steps.forEach(step => tr.step(step));
        tr.setSelection(Selection.near(tr.doc.resolve(listTr.selection.from)));
        tr.setStoredMarks(state.storedMarks || $from.marks());
        dispatch(tr.scrollIntoView());
      })
  );
};
